import React from "react"
import { Flex, FlexProps, Grid, GridProps } from "@chakra-ui/core"
import { CompareBike } from "../../views/BicycleProductList"
import CompareColumnHeader from "./CompareColumnHeader"
import { ContentfulBicycleModel } from "../../../graphql-types"
import SmallCaps from "../typography/SmallCaps"
import TextCell from "./cells/TextCell"
import OptionsCell from "./cells/OptionsCell"
import DistanceCell from "./cells/DistanceCell"
import WeightCell from "./cells/WeightCell"
import HeightCell from "./cells/HeightCell"

const CompareTableHeader = (props: FlexProps) => (
  <Flex
    position="sticky"
    top="0"
    left="0"
    h="100%"
    justify="center"
    backgroundColor="white"
    pb="2.1875rem"
    borderStyle="solid"
    borderRightWidth="1px"
    borderBottomWidth="1px"
    borderColor="dividerLine"
    {...props}
  />
)

type CompareTableCellProps = FlexProps & {
  hovered?: boolean
}
const CompareTableCell = ({
  hovered,
  backgroundColor = "white",
  ...props
}: CompareTableCellProps) => (
  <Flex
    h="7rem"
    backgroundColor={hovered ? "#F9F9FA" : backgroundColor}
    borderStyle="solid"
    borderRightWidth="1px"
    borderBottomWidth="1px"
    borderColor="dividerLine"
    align="center"
    justify="center"
    {...props}
  />
)

type CompareTableRowProps = {
  bikes: CompareBike[]
  label: string
  name?: string
  Cell?: React.FC
  cellProps?: any
}
export const CompareTableRow = ({
  bikes,
  label,
  name,
  Cell = TextCell,
  cellProps,
}: CompareTableRowProps) => {
  const [hover, setHover] = React.useState(false)
  const onHover = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  }

  const values = bikes.map((bike, index) => {
    const speed = bike.variant.speed
    const product = bike.bike?.speeds?.find(
      (product) => product?.speed === speed
    )
    return product ? (
      <Cell
        key={index}
        bike={bike.bike}
        product={product}
        value={name ? product?.[name] : undefined}
        {...cellProps}
      />
    ) : (
      <TextCell key={index} value="N/A" />
    )
  })

  return (
    <>
      <CompareTableCell
        backgroundColor="backgroundOpaque"
        hovered={hover}
        {...onHover}
      >
        <SmallCaps size="lg" fontWeight="bold">
          {label}
        </SmallCaps>
      </CompareTableCell>
      <CompareTableCell {...onHover} hovered={hover}>
        {values[0]}
      </CompareTableCell>
      <CompareTableCell {...onHover} hovered={hover}>
        {values[1]}
      </CompareTableCell>
      <CompareTableCell {...onHover} hovered={hover} borderRightWidth="0px">
        {values[2]}
      </CompareTableCell>
    </>
  )
}

type CompareTableProps = GridProps & {
  bikes: CompareBike[]
  onSpeedChange?: (bikeIndex: number, speed: string) => void
}
export default function CompareTable({
  bikes,
  onSpeedChange,
  ...props
}: CompareTableProps) {
  return (
    <Grid
      w="100%"
      pb="12.5rem"
      gridTemplateColumns="minmax(240px, 0.5fr) repeat(3,1fr)"
      {...props}
    >
      {/* Header */}
      <CompareTableHeader backgroundColor="backgroundOpaque" />
      <CompareTableHeader>
        {bikes[0]?.bike && (
          <CompareColumnHeader
            bike={bikes[0]?.bike as ContentfulBicycleModel}
            defaultSpeed={bikes[0].variant.speed}
            variant={bikes[0].variant}
            onSpeedChange={(speed) => {
              onSpeedChange?.(0, speed)
            }}
          />
        )}
      </CompareTableHeader>
      <CompareTableHeader>
        {bikes[1]?.bike && (
          <CompareColumnHeader
            bike={bikes[1]?.bike as ContentfulBicycleModel}
            defaultSpeed={bikes[1].variant.speed}
            variant={bikes[1].variant}
            onSpeedChange={(speed) => {
              onSpeedChange?.(1, speed)
            }}
          />
        )}
      </CompareTableHeader>
      <CompareTableHeader borderRightWidth="0px">
        {bikes[2]?.bike && (
          <CompareColumnHeader
            bike={bikes[2]?.bike as ContentfulBicycleModel}
            defaultSpeed={bikes[2].variant.speed}
            variant={bikes[2].variant}
            onSpeedChange={(speed) => {
              onSpeedChange?.(2, speed)
            }}
          />
        )}
      </CompareTableHeader>
      {/* Data Rows */}
      <CompareTableRow bikes={bikes} label="Weight Range" Cell={WeightCell} />
      <CompareTableRow bikes={bikes} label="Height Range" Cell={HeightCell} />
      <CompareTableRow
        bikes={bikes}
        label="Arm Length"
        name="armLength"
        Cell={OptionsCell}
        cellProps={{ options: ["Short", "Average", "Long"] }}
      />
      <CompareTableRow
        bikes={bikes}
        label="Leg Length"
        name="legLength"
        Cell={OptionsCell}
        cellProps={{ options: ["Short", "Average", "Long"] }}
      />
      <CompareTableRow
        bikes={bikes}
        label="Ride Frequency"
        name="rideFrequency"
        Cell={OptionsCell}
        cellProps={{ options: ["Daily", "Once a Week", "Once a Month"] }}
      />
      <CompareTableRow
        bikes={bikes}
        label="Primary Use"
        name="primaryUse"
        Cell={OptionsCell}
        cellProps={{ options: ["Recreation", "Commuting", "Exercise"] }}
      />
      <CompareTableRow
        bikes={bikes}
        label="Riding Surface"
        name="ridingSurface"
        Cell={OptionsCell}
        cellProps={{ options: ["Paved Surfaces", "Paved Roads"] }}
      />
      <CompareTableRow bikes={bikes} label="Distance" Cell={DistanceCell} />
      <CompareTableRow
        bikes={bikes}
        label="Riding Pace"
        name="ridingPace"
        Cell={OptionsCell}
        cellProps={{ options: ["Leisurely", "Moderate", "Aggressive"] }}
      />
      <CompareTableRow
        bikes={bikes}
        label="Designed For"
        name="ridePreference"
        Cell={OptionsCell}
        cellProps={{ options: ["Comfort", "Speed"] }}
      />
      <CompareTableRow
        bikes={bikes}
        label="Seat Firmness"
        name="seatPreference"
      />
      <CompareTableRow
        bikes={bikes}
        label="Uphill Frequency"
        name="uphillFrequency"
      />
      <CompareTableRow bikes={bikes} label="Body Pain" name="bodyPain" />
    </Grid>
  )
}
